import React from "react";
import { EditIcon } from "../../../../../icons";
import { getWatermarkFontStack } from "../../../watermark/Popup/watermarkSettings";
import {
  DEFAULT_SIGN_PDF_CANVAS_THEME_ID,
  getSignPdfCanvasTheme,
  hasSignPdfSignature,
  SIGN_PDF_MODES,
  signPdfModeFromSettings,
} from "./signPdfSettings";
import { SIGN_PDF_FIELD_KINDS } from "./signPdfPlacements";
import "./SignPdfControls.css";

const DragHandleIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <circle cx="5" cy="4" r="1.3" fill="currentColor" />
    <circle cx="11" cy="4" r="1.3" fill="currentColor" />
    <circle cx="5" cy="8" r="1.3" fill="currentColor" />
    <circle cx="11" cy="8" r="1.3" fill="currentColor" />
    <circle cx="5" cy="12" r="1.3" fill="currentColor" />
    <circle cx="11" cy="12" r="1.3" fill="currentColor" />
  </svg>
);

const DateFieldIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <rect
      x="3"
      y="4"
      width="14"
      height="13"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M3 8h14M7 2.5v3M13 2.5v3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const TextFieldIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <path
      d="M7 3h6M10 3v14M7.2 17h5.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M8.2 7.2h3.6M8.2 12.8h3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      opacity="0.55"
    />
  </svg>
);

const CheckboxFieldIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <rect
      x="3.5"
      y="3.5"
      width="13"
      height="13"
      rx="2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M6.5 10.2l2.4 2.4 4.6-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function isLightSignColor(color) {
  const c = String(color || "").trim().toLowerCase();
  return (
    c === "#fff" ||
    c === "#ffffff" ||
    c === "white" ||
    c === "rgb(255, 255, 255)" ||
    c === "rgba(255, 255, 255, 1)"
  );
}

function MarkPreview({ settings }) {
  const text = String(settings?.text || "").trim();
  const mode = signPdfModeFromSettings(settings);
  const showImage =
    Boolean(settings?.logoUrl) &&
    (mode === SIGN_PDF_MODES.UPLOAD || mode === SIGN_PDF_MODES.DRAW);

  const themeId =
    settings?.signPdfCanvasTheme ||
    (isLightSignColor(settings?.color)
      ? "black_grey"
      : DEFAULT_SIGN_PDF_CANVAS_THEME_ID);
  const canvasTheme = getSignPdfCanvasTheme(themeId);
  const previewBg =
    canvasTheme.id === "white_grey" ? "#f7f7f4" : "#0a0a0a";

  if (showImage) {
    return (
      <div
        className="sp-summary__preview sp-summary__preview--image"
        style={{ backgroundColor: previewBg }}
      >
        <img src={settings.logoUrl} alt="" />
      </div>
    );
  }

  return (
    <div
      className={`sp-summary__preview sp-summary__preview--text sp-summary__preview--${canvasTheme.id}`}
      style={{ backgroundColor: previewBg }}
    >
      <span
        style={{
          fontFamily: getWatermarkFontStack(settings.fontId),
          color: canvasTheme.textColor,
          WebkitTextFillColor: canvasTheme.textColor,
        }}
      >
        {text || "Signature"}
      </span>
    </div>
  );
}

/**
 * Palette-style field row — drag handle, accent, preview, edit.
 */
const SignPdfFieldPaletteRow = ({
  field,
  templateSettings,
  disabled,
  onPlace,
  onEdit,
}) => {
  const isSig =
    field.id === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    field.id === SIGN_PDF_FIELD_KINDS.INITIALS;
  const hasTemplate = isSig ? hasSignPdfSignature(templateSettings) : true;

  const onDragStart = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (isSig && !hasTemplate) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("application/x-sign-pdf-field", field.id);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleClick = () => {
    if (disabled) return;
    if (isSig && !hasTemplate) {
      onEdit?.(field.id);
      return;
    }
    onPlace?.(field.id);
  };

  let body = null;
  if (isSig && hasTemplate) {
    body = <MarkPreview settings={templateSettings} />;
  } else if (isSig) {
    body = (
      <span className="sp-palette__empty">
        Add {field.label.toLowerCase()}
      </span>
    );
  } else if (field.id === SIGN_PDF_FIELD_KINDS.DATE) {
    body = (
      <>
        <span className="sp-palette__type-icon" aria-hidden="true">
          <DateFieldIcon />
        </span>
        <span className="sp-palette__label">{field.label}</span>
      </>
    );
  } else if (field.id === SIGN_PDF_FIELD_KINDS.TEXT) {
    body = (
      <>
        <span className="sp-palette__type-icon" aria-hidden="true">
          <TextFieldIcon />
        </span>
        <span className="sp-palette__label">{field.label}</span>
      </>
    );
  } else {
    body = (
      <>
        <span className="sp-palette__type-icon" aria-hidden="true">
          <CheckboxFieldIcon />
        </span>
        <span className="sp-palette__label">{field.label}</span>
      </>
    );
  }

  return (
    <div
      className={`sp-palette__row${isSig && hasTemplate ? " has-preview" : ""}`}
      draggable={!disabled && (!isSig || hasTemplate)}
      onDragStart={onDragStart}
    >
      <span className="sp-palette__accent" aria-hidden="true" />
      <span className="sp-palette__handle" aria-hidden="true">
        <DragHandleIcon />
      </span>
      <button
        type="button"
        className="sp-palette__main"
        disabled={disabled}
        onClick={handleClick}
        title={
          isSig && !hasTemplate
            ? `Create ${field.label.toLowerCase()}`
            : `Place ${field.label.toLowerCase()}`
        }
      >
        {body}
      </button>
      {field.editable ? (
        <button
          type="button"
          className="sp-palette__edit"
          disabled={disabled}
          onClick={() => onEdit?.(field.id)}
          aria-label={`Edit ${field.label.toLowerCase()}`}
          title="Edit"
        >
          <EditIcon size={16} />
        </button>
      ) : null}
    </div>
  );
};

export default SignPdfFieldPaletteRow;
